// Renders the SEO tag templates (resources/views/seo/<page>/seo-tags.blade.php
// in Laravel, editable from admin > settings > seo). Supports the blade subset
// those templates use: {{ }}, {!! !!}, @if/@elseif/@else/@endif,
// @isset/@endisset, @foreach/@endforeach and php expressions with variables,
// array access, ??, ?:, ., !, &&, ||, comparisons and the settings(), __(),
// isset(), empty(), url() and urls()->...() helpers.
//
// Plain typescript: used by the web app (src/common/seo/render-seo-tags.ts)
// and the "seo" edge function that prerenders pages for crawlers.

export interface BladeContext {
  vars: Record<string, unknown>;
  baseUrl: string;
  setting: (name: string) => unknown;
  translate?: (text: string) => string;
  slugify: (text: string) => string;
}

export function escapeHtml(value: unknown): string {
  return toText(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function toText(value: unknown): string {
  if (value === null || value === undefined || value === false) return '';
  if (value === true) return '1';
  if (typeof value === 'object') return '';
  return String(value);
}

// ---------------------------------------------------------------------------
// urls()->... (App\Core\UrlGenerator)
// ---------------------------------------------------------------------------
function urlGenerator(ctx: BladeContext) {
  const url = (path = '') => `${ctx.baseUrl.replace(/\/$/, '')}${path ? `/${path.replace(/^\//, '')}` : ''}`;
  const get = (model: any, key: string) => (model && typeof model === 'object' ? model[key] : undefined);
  return {
    url,
    article: (article: any) => url(`hc/articles/${get(article, 'id')}/${ctx.slugify(toText(get(article, 'title')))}`),
    category: (category: any) => url(`hc/categories/${get(category, 'id')}/${ctx.slugify(toText(get(category, 'name')))}`),
    customPage: (page: any) => url(`pages/${ctx.slugify(toText(get(get(page, 'page') ?? page, 'slug')))}`),
    home: () => url(''),
    search: (query?: string) => url(query ? `hc/search/${encodeURIComponent(query)}` : 'hc/search'),
  } as Record<string, (...args: any[]) => string>;
}

// ---------------------------------------------------------------------------
// expressions
// ---------------------------------------------------------------------------
type Token = {type: 'var' | 'name' | 'string' | 'number' | 'op' | 'eof'; value: string};

function tokenize(source: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;
  while (i < source.length) {
    const char = source[i];
    if (/\s/.test(char)) {
      i++;
    } else if (char === '$') {
      const match = /^\$[A-Za-z_]\w*/.exec(source.slice(i));
      if (!match) throw new Error('Invalid variable');
      tokens.push({type: 'var', value: match[0].slice(1)});
      i += match[0].length;
    } else if (char === "'" || char === '"') {
      let value = '';
      i++;
      while (i < source.length && source[i] !== char) {
        if (source[i] === '\\' && i + 1 < source.length) {
          value += source[i + 1];
          i += 2;
        } else {
          value += source[i++];
        }
      }
      i++;
      tokens.push({type: 'string', value});
    } else if (/\d/.test(char)) {
      const match = /^\d+(\.\d+)?/.exec(source.slice(i))!;
      tokens.push({type: 'number', value: match[0]});
      i += match[0].length;
    } else if (/[A-Za-z_\\]/.test(char)) {
      const match = /^[A-Za-z_\\][\w\\]*(::\w+)?/.exec(source.slice(i))!;
      tokens.push({type: 'name', value: match[0]});
      i += match[0].length;
    } else {
      const op = ['===', '!==', '??', '==', '!=', '<=', '>=', '&&', '||', '->', '?:'].find(o => source.startsWith(o, i)) ?? char;
      tokens.push({type: 'op', value: op});
      i += op.length;
    }
  }
  tokens.push({type: 'eof', value: ''});
  return tokens;
}

const MISSING = Symbol('missing');

class Parser {
  private pos = 0;
  constructor(private tokens: Token[], private ctx: BladeContext) {}

  private peek() {
    return this.tokens[this.pos];
  }
  private next() {
    return this.tokens[this.pos++];
  }
  private accept(type: Token['type'], value?: string) {
    const token = this.peek();
    if (token.type === type && (value === undefined || token.value.toLowerCase() === value)) {
      this.pos++;
      return token;
    }
    return null;
  }
  private expect(type: Token['type'], value?: string) {
    const token = this.accept(type, value);
    if (!token) throw new Error(`Expected ${value ?? type}`);
    return token;
  }

  parse(): unknown {
    const value = this.ternary();
    this.expect('eof');
    return value === MISSING ? null : value;
  }

  private ternary(): unknown {
    const condition = this.coalesce();
    if (this.accept('op', '?:')) {
      const fallback = this.ternary();
      return truthy(condition) ? condition : fallback;
    }
    if (this.accept('op', '?')) {
      if (this.accept('op', ':')) {
        const fallback = this.ternary();
        return truthy(condition) ? condition : fallback;
      }
      const whenTrue = this.ternary();
      this.expect('op', ':');
      const whenFalse = this.ternary();
      return truthy(condition) ? whenTrue : whenFalse;
    }
    return condition;
  }

  private coalesce(): unknown {
    const left = this.or();
    if (this.accept('op', '??')) {
      const right = this.coalesce();
      return left === MISSING || left === null || left === undefined ? right : left;
    }
    return left;
  }

  private or(): unknown {
    let left = this.and();
    while (this.accept('op', '||') || this.accept('name', 'or')) {
      const right = this.and();
      left = truthy(left) || truthy(right);
    }
    return left;
  }

  private and(): unknown {
    let left = this.comparison();
    while (this.accept('op', '&&') || this.accept('name', 'and')) {
      const right = this.comparison();
      left = truthy(left) && truthy(right);
    }
    return left;
  }

  private comparison(): unknown {
    const left = this.concat();
    const token = this.peek();
    if (token.type === 'op' && ['===', '!==', '==', '!=', '<', '>', '<=', '>='].includes(token.value)) {
      this.next();
      const right = this.concat();
      const a = clean(left);
      const b = clean(right);
      switch (token.value) {
        case '===':
          return a === b;
        case '!==':
          return a !== b;
        case '==':
          return a == b;
        case '!=':
          return a != b;
        case '<':
          return (a as any) < (b as any);
        case '>':
          return (a as any) > (b as any);
        case '<=':
          return (a as any) <= (b as any);
        default:
          return (a as any) >= (b as any);
      }
    }
    return left;
  }

  private concat(): unknown {
    let left = this.unary();
    while (this.accept('op', '.')) {
      left = toText(clean(left)) + toText(clean(this.unary()));
    }
    return left;
  }

  private unary(): unknown {
    if (this.accept('op', '!')) return !truthy(this.unary());
    return this.postfix(this.primary());
  }

  private postfix(value: unknown): unknown {
    for (;;) {
      if (this.accept('op', '[')) {
        const key = clean(this.ternary());
        this.expect('op', ']');
        value = value && typeof value === 'object' && key != null && (key as any) in (value as any) ? (value as any)[key as any] : MISSING;
      } else if (this.accept('op', '->')) {
        const name = this.expect('name').value;
        if (this.accept('op', '(')) {
          const args = this.args();
          const method = value && typeof value === 'object' ? (value as any)[name] : undefined;
          value = typeof method === 'function' ? method(...args) : MISSING;
        } else {
          value = value && typeof value === 'object' && name in (value as any) ? (value as any)[name] : MISSING;
        }
      } else {
        return value;
      }
    }
  }

  private args(): unknown[] {
    const args: unknown[] = [];
    if (this.accept('op', ')')) return args;
    do {
      args.push(clean(this.ternary()));
    } while (this.accept('op', ','));
    this.expect('op', ')');
    return args;
  }

  private primary(): unknown {
    const token = this.next();
    switch (token.type) {
      case 'var':
        return token.value in this.ctx.vars ? this.ctx.vars[token.value] : MISSING;
      case 'string':
        return token.value;
      case 'number':
        return Number(token.value);
      case 'op':
        if (token.value === '(') {
          const value = this.ternary();
          this.expect('op', ')');
          return value;
        }
        if (token.value === '[') {
          // [] / ['a', 'b'] array literals
          const items: unknown[] = [];
          if (!this.accept('op', ']')) {
            do items.push(clean(this.ternary()));
            while (this.accept('op', ','));
            this.expect('op', ']');
          }
          return items;
        }
        throw new Error(`Unexpected ${token.value}`);
      case 'name':
        return this.call(token.value);
      default:
        throw new Error('Unexpected end of expression');
    }
  }

  private call(name: string): unknown {
    const lower = name.toLowerCase();
    if (lower === 'true') return true;
    if (lower === 'false') return false;
    if (lower === 'null') return null;
    if (!this.accept('op', '(')) return MISSING;

    // isset() / empty() look at the raw value, missing keys don't throw
    if (lower === 'isset' || lower === 'empty') {
      const values: unknown[] = [];
      if (!this.accept('op', ')')) {
        do values.push(this.ternary());
        while (this.accept('op', ','));
        this.expect('op', ')');
      }
      const set = values.every(v => v !== MISSING && v !== null && v !== undefined);
      return lower === 'isset' ? set : !values.every(v => truthy(v));
    }

    const args = this.args();
    switch (lower) {
      case 'settings':
      case 'config':
        return this.ctx.setting(String(args[0] ?? '')) ?? args[1] ?? null;
      case '__':
      case 'trans':
      case '@lang':
        return this.ctx.translate ? this.ctx.translate(toText(args[0])) : toText(args[0]);
      case 'urls':
        return urlGenerator(this.ctx);
      case 'url':
        return urlGenerator(this.ctx).url(toText(args[0]));
      case 'strip_tags':
        return toText(args[0]).replace(/<[^>]*>/g, '');
      case 'str_limit':
      case 'str\\limit':
      case 'str::limit': {
        const text = toText(args[0]);
        const limit = Number(args[1] ?? 100);
        return text.length > limit ? `${text.slice(0, limit)}${args[2] ?? '...'}` : text;
      }
      case 'strtolower':
        return toText(args[0]).toLowerCase();
      case 'strtoupper':
        return toText(args[0]).toUpperCase();
      case 'ucfirst':
        return toText(args[0]).charAt(0).toUpperCase() + toText(args[0]).slice(1);
      case 'trim':
        return toText(args[0]).trim();
      case 'count':
        return Array.isArray(args[0]) ? args[0].length : args[0] && typeof args[0] === 'object' ? Object.keys(args[0]).length : 0;
      case 'json_encode':
        return JSON.stringify(args[0] ?? null);
      case 'e':
        return escapeHtml(args[0]);
      default:
        return MISSING;
    }
  }
}

function clean(value: unknown) {
  return value === MISSING ? null : value;
}

function truthy(value: unknown) {
  value = clean(value);
  if (Array.isArray(value)) return value.length > 0;
  return !!value && value !== '0';
}

function evaluate(expression: string, ctx: BladeContext): unknown {
  try {
    return new Parser(tokenize(expression), ctx).parse();
  } catch {
    // templates are edited by admins, a broken expression renders nothing
    return null;
  }
}

// ---------------------------------------------------------------------------
// template
// ---------------------------------------------------------------------------
type Node =
  | {type: 'text'; value: string}
  | {type: 'echo'; expression: string; raw: boolean}
  | {type: 'if'; branches: {condition: string | null; body: Node[]}[]}
  | {type: 'foreach'; list: string; key: string | null; item: string; body: Node[]};

// "(...)" argument of a directive, with nested parentheses and strings
function readParens(source: string, start: number): [string, number] {
  let depth = 0;
  let quote: string | null = null;
  for (let i = start; i < source.length; i++) {
    const char = source[i];
    if (quote) {
      if (char === '\\') i++;
      else if (char === quote) quote = null;
    } else if (char === "'" || char === '"') {
      quote = char;
    } else if (char === '(') {
      depth++;
    } else if (char === ')') {
      depth--;
      if (depth === 0) return [source.slice(start + 1, i), i + 1];
    }
  }
  return [source.slice(start + 1), source.length];
}

function parseTemplate(source: string): Node[] {
  source = source.replace(/\{\{--[\s\S]*?--\}\}/g, '');
  const root: Node[] = [];
  const stack: {node: Node | null; body: Node[]}[] = [{node: null, body: root}];
  const current = () => stack[stack.length - 1];
  const directive = /\{!!([\s\S]*?)!!\}|\{\{([\s\S]*?)\}\}|@(if|elseif|else|endif|isset|endisset|foreach|endforeach|unless|endunless)\b/g;
  let last = 0;
  let match: RegExpExecArray | null;

  while ((match = directive.exec(source))) {
    // "@{{ x }}" renders the braces literally
    if (match[2] !== undefined && source[match.index - 1] === '@') {
      current().body.push({type: 'text', value: source.slice(last, match.index - 1) + match[0]});
      last = directive.lastIndex;
      continue;
    }
    if (match.index > last) current().body.push({type: 'text', value: source.slice(last, match.index)});
    last = directive.lastIndex;

    if (match[1] !== undefined || match[2] !== undefined) {
      current().body.push({type: 'echo', expression: (match[1] ?? match[2]).trim(), raw: match[1] !== undefined});
      continue;
    }

    const name = match[3];
    let argument = '';
    if (['if', 'elseif', 'isset', 'foreach', 'unless'].includes(name)) {
      let i = last;
      while (source[i] === ' ') i++;
      if (source[i] === '(') {
        [argument, last] = readParens(source, i);
        directive.lastIndex = last;
      }
    }

    if (name === 'if' || name === 'isset' || name === 'unless') {
      const condition = name === 'isset' ? `isset(${argument})` : name === 'unless' ? `!(${argument})` : argument;
      const node: Node = {type: 'if', branches: [{condition, body: []}]};
      current().body.push(node);
      stack.push({node, body: node.branches[0].body});
    } else if ((name === 'elseif' || name === 'else') && current().node?.type === 'if') {
      const node = current().node as Extract<Node, {type: 'if'}>;
      const branch = {condition: name === 'else' ? null : argument, body: [] as Node[]};
      node.branches.push(branch);
      stack[stack.length - 1] = {node, body: branch.body};
    } else if (name === 'foreach') {
      const parts = /^([\s\S]+?)\s+as\s+(?:\$(\w+)\s*=>\s*)?\$(\w+)\s*$/.exec(argument.trim());
      const node: Node = {type: 'foreach', list: parts?.[1] ?? '[]', key: parts?.[2] ?? null, item: parts?.[3] ?? 'item', body: []};
      current().body.push(node);
      stack.push({node, body: node.body});
    } else if (['endif', 'endisset', 'endunless', 'endforeach'].includes(name) && stack.length > 1) {
      stack.pop();
    }
  }
  if (last < source.length) current().body.push({type: 'text', value: source.slice(last)});
  return root;
}

function renderNodes(nodes: Node[], ctx: BladeContext): string {
  let output = '';
  for (const node of nodes) {
    if (node.type === 'text') {
      output += node.value;
    } else if (node.type === 'echo') {
      const value = evaluate(node.expression, ctx);
      output += node.raw ? toText(value) : escapeHtml(value);
    } else if (node.type === 'if') {
      const branch = node.branches.find(b => b.condition === null || truthy(evaluate(b.condition, ctx)));
      if (branch) output += renderNodes(branch.body, ctx);
    } else {
      const list = evaluate(node.list, ctx);
      const entries: [unknown, unknown][] = Array.isArray(list)
        ? list.map((item, i) => [i, item])
        : list && typeof list === 'object'
          ? Object.entries(list)
          : [];
      for (const [key, item] of entries) {
        const vars = {...ctx.vars, [node.item]: item, ...(node.key ? {[node.key]: key} : {})};
        output += renderNodes(node.body, {...ctx, vars});
      }
    }
  }
  return output;
}

export function renderBlade(template: string, ctx: BladeContext): string {
  return renderNodes(parseTemplate(template ?? ''), ctx);
}
